import { Device, DeviceStatusInfo, User } from "@fairfleet/geotab";
import PersonSharp from "@mui/icons-material/PersonSharp";
import Autocomplete from "@mui/material/Autocomplete";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import { TFunction } from "i18next";
import { HtmlHTMLAttributes, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounced } from "../../hooks/useDebounced";
import { DeviceActivityIcon } from "@/components/DeviceActivityIcon";
import { useDevicesBySearch } from "@/hooks/devices/useDevicesBySearch";
import getDeviceLicensePlate from "@/hooks/getDeviceLicensePlate";
import { useStatusInfosByIds } from "@/hooks/statusInfos/useStatusInfosByIds";
import { useUsersBySearch } from "@/hooks/users/useUsersBySearch";
import { deviceActivity } from "@/utilities/deviceActivity";
import { getUserDisplayName } from "@/utilities/user/getUserDisplayName";

type Option = OptionDevice | OptionDriver;
type OptionDevice = { type: "device"; device: Device };
type OptionDriver = { type: "user"; driver: User };

interface Props {
  /** The selected {@link User}s. */
  users?: User[];
  /** The selected {@link Device}s. */
  devices?: Device[];
  /** Called when the selection changes. */
  onChange?(users?: User[], devices?: Device[]): void;
  onlyDrivers: boolean;
  showArchived: boolean;
  showArchivedVehicles?: boolean;
  /**
   * Pass `false` on a page that is about a vehicle, where a driver cannot be the answer. The
   * driver query is then not made at all.
   */
  showUsers?: boolean;
}

/**
 * A select input for selecting a {@link User} or a {@link Device}.
 */
export function DriverOrDeviceSelect({
  users: selectedUsers = [],
  devices: selectedDevices = [],
  onChange,
  onlyDrivers,
  showArchived,
  showArchivedVehicles,
  showUsers = true,
}: Props) {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const searchDebounced = useDebounced(search, 300);
  const { t } = useTranslation();
  const { data: drivers = [], isLoading: isDriversLoading } = useUsersBySearch(
    searchDebounced,
    onlyDrivers,
    showArchived,
    false,
    showUsers,
  );
  const { data: deviceData, isLoading: isDevicesLoading } = useDevicesBySearch(
    searchDebounced,
    showArchivedVehicles,
  );
  const devices = deviceData ?? [];

  // Taken from the query data rather than from `devices`, which is a fresh array on every render,
  // and sorted, because this array is part of the status query key: in search order it would
  // change whenever two vehicles swapped places and refetch for nothing.
  //
  // Empty while the list is closed, which switches the query off. The status is only ever seen
  // inside the open list, and asking for it regardless would poll a fleet-sized set of vehicles
  // every fifteen seconds — in the background too — for as long as the page stays open.
  const deviceIds = useMemo(
    () => (isOpen ? (deviceData ?? []).map(device => device.id).sort() : []),
    [deviceData, isOpen],
  );
  const { data: statusInfoMap = {} } = useStatusInfosByIds(deviceIds);

  const value = selectedUsers
    .map(driver => ({ type: "user", driver }) as Option)
    .concat(selectedDevices.map(device => ({ type: "device", device }) as Option));

  const selectedType = getSelectedType(value);
  const options = [
    ...drivers.map(driver => ({ type: "user", driver }) as OptionDriver),
    ...devices.map(device => ({ type: "device", device }) as OptionDevice),
  ];

  function onOptionChange(_: unknown, value: Option[] | null) {
    const drivers = value?.filter((o): o is OptionDriver => o.type === "user").map(o => o.driver);
    const devices = value?.filter((o): o is OptionDevice => o.type === "device").map(o => o.device);

    onChange?.(drivers, devices);
  }

  return (
    <Autocomplete
      id="driver-or-device-select"
      size="small"
      multiple
      fullWidth
      loading={isDriversLoading || isDevicesLoading}
      autoComplete
      disableCloseOnSelect
      includeInputInList
      onChange={onOptionChange}
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      limitTags={1}
      options={options}
      groupBy={option => groupBy(t, option)}
      getOptionLabel={option => (typeof option === "string" ? option : getOptionLabel(option))}
      filterOptions={o => o}
      filterSelectedOptions
      renderOption={(props, option) => renderOption(props, option, statusInfoMap)}
      getOptionDisabled={option => (selectedType && option.type !== selectedType) ?? false}
      // input
      onInputChange={(_, value) => setSearch(value)}
      renderInput={params => (
        <TextField
          {...params}
          type="search"
          label={t("search.title")}
          fullWidth
          placeholder={
            showUsers ? t("search.deviceOrDriver") : t("search.device", "Suche nach Fahrzeug")
          }
        />
      )}
    />
  );
}

function getSelectedType(selected?: Option[]) {
  if (Array.isArray(selected)) {
    return selected[0]?.type;
  }
}

function getOptionLabel(option: Option) {
  if (option.type === "user") return getUserDisplayName(option.driver);
  if (option.type === "device") return option.device.name;

  return "";
}

function groupBy(t: TFunction, option: Option) {
  if (option.type === "user") return t("logbookEdit.driver");
  if (option.type === "device") return t("logbookEdit.device");

  return "";
}

function renderOption(
  props: HtmlHTMLAttributes<HTMLLIElement>,
  option: Option,
  statusInfoMap: Record<string, DeviceStatusInfo>,
) {
  return (
    <li {...props} key={option.type === "user" ? option.driver.id : option.device.id}>
      {option.type === "user" && renderDriver(option)}
      {option.type === "device" && renderDevice(option, statusInfoMap[option.device.id])}
    </li>
  );
}

const OptionStack = styled("div")`
  display: flex;
  flex-direction: row;
  gap: 12px;
  margin-left: -16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

function renderDriver(option: OptionDriver) {
  const isActive = new Date(option.driver.activeTo) >= new Date();
  return (
    <OptionStack>
      <PersonSharp style={{ color: isActive ? "inherit" : "gray" }} />
      {getUserDisplayName(option.driver)}
    </OptionStack>
  );
}

/**
 * A vehicle option: what it is doing right now, then its name.
 *
 * The same mark the live data page puts in front of a vehicle. Which vehicle is moving decides
 * whether its trips are still being written, and reading that off the list beats selecting a
 * vehicle to find out.
 */
function renderDevice(option: OptionDevice, status: DeviceStatusInfo | undefined) {
  const isActive = new Date(option.device.activeTo) >= new Date();
  const licensePlate = getDeviceLicensePlate(option.device);

  return (
    <OptionStack>
      <DeviceActivityIcon activity={deviceActivity(status)} />
      <span style={{ color: isActive ? "inherit" : "gray" }}>
        {option.device.name}
        {licensePlate ? ` · ${licensePlate}` : ""}
      </span>
    </OptionStack>
  );
}
